import React, { useEffect } from 'react';
import { Home, Check, Layout, Bed, Bath, Ruler, Building, Car, TreePine, Calendar } from 'lucide-react';
import { propertyLabels } from './InfPublish';

const CarPublish = ({ 
  formData, 
  handleInputChange, 
  setFormData 
}) => {
  // Manejar cambios en área ocupada con validación estricta
  const handleAreaOcupadaChange = (e) => {
    let value = e.target.value;
    
    // Permitir valor vacío (que se convertirá en 0)
    if (value === '') {
      setFormData(prev => ({
        ...prev,
        area_ocupada: '0'
      }));
      return;
    }
    
    // Convertir a número para comparación
    let numValue = parseFloat(value);
    const areaTotal = parseFloat(formData.area_total) || 0;
    
    // Si es negativo, convertir a 0
    if (numValue < 0) {
      setFormData(prev => ({
        ...prev,
        area_ocupada: '0'
      }));
      return;
    }
    
    // Asegurarse que el área ocupada no sea mayor que el área total
    if (numValue <= areaTotal) {
      setFormData(prev => ({
        ...prev,
        area_ocupada: numValue.toString()
      }));
    } else {
      // Si es mayor, establecer el valor máximo como el área total
      setFormData(prev => ({
        ...prev,
        area_ocupada: areaTotal.toString()
      }));
    }
  };

  // Asegurarse que al cargar el componente o cuando cambie el área total, 
  // area_ocupada tenga 0 como valor por defecto si está vacío o sea mayor que el área total
  useEffect(() => {
    const areaTotal = parseFloat(formData.area_total) || 0;
    const areaOcupada = parseFloat(formData.area_ocupada) || 0;
    
    if (formData.area_ocupada === '' || formData.area_ocupada === undefined) {
      // Si el campo está vacío, establecer a 0
      setFormData(prev => ({
        ...prev,
        area_ocupada: '0'
      }));
    } else if (areaOcupada > areaTotal) {
      // Si el área ocupada es mayor que el área total, ajustar al máximo permitido
      setFormData(prev => ({
        ...prev,
        area_ocupada: areaTotal.toString()
      }));
    }
  }, [formData.area_total, formData.area_ocupada]);

  return (
    <div className="space-y-8">
      {/* Header mejorado */}
      <div className="text-center mb-8">
        <div className="relative mb-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-0.5 w-12 bg-gradient-to-r from-transparent to-purple-500 rounded-full"></div>
            <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-lg">
              <Home className="w-6 h-6 text-white" />
            </div>
            <div className="h-0.5 w-12 bg-gradient-to-l from-transparent to-indigo-500 rounded-full"></div>
          </div>
          
          <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 
                         bg-clip-text text-transparent mb-3">
            Características de la Propiedad
          </h2>
          
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
            <div className="w-16 h-0.5 bg-gradient-to-r from-purple-400 to-indigo-400 rounded-full"></div>
            <div className="w-2 h-2 bg-indigo-400 rounded-full animate-pulse delay-75"></div>
          </div>
        </div>
        
        <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
          Define las medidas, estructura y características específicas de tu propiedad
        </p>
      </div>

      {/* Medidas del terreno */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
        <div className="flex items-center gap-3 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100 rounded-t-2xl">
          <div className="p-2 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl">
            <Ruler className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Medidas del Terreno</h3>
            <p className="text-gray-500 text-sm">Dimensiones y área total de la propiedad</p>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Dimensiones */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                {propertyLabels[formData.tipo]?.largo || "Largo del Terreno (m)*"}
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="largo_terreno"
                  value={formData.largo_terreno}
                  onChange={handleInputChange}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 
                             focus:border-blue-500 transition-all duration-200 text-lg font-medium
                             bg-gradient-to-r from-blue-50 to-white hover:border-blue-300"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                  <span className="text-blue-600 font-bold">m</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                {propertyLabels[formData.tipo]?.ancho || "Ancho del Terreno (m)*"}
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="ancho_terreno"
                  value={formData.ancho_terreno}
                  onChange={handleInputChange}
                  className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 
                             focus:border-blue-500 transition-all duration-200 text-lg font-medium
                             bg-gradient-to-r from-blue-50 to-white hover:border-blue-300"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                  <span className="text-blue-600 font-bold">m</span>
                </div>
              </div>
            </div>
          </div>

          {/* Áreas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                {propertyLabels[formData.tipo]?.areaTotal || "Área Total (m²)"}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.area_total}
                  readOnly
                  className="w-full p-4 border-2 border-gray-200 rounded-xl bg-gradient-to-r from-gray-100 to-gray-50 
                             text-lg font-bold text-gray-600 cursor-not-allowed"
                />
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                  <span className="text-gray-600 font-bold">m²</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-gray-700">
                  {propertyLabels[formData.tipo]?.areaOcupada || "Área Ocupada (m²)*"}
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-purple-600 cursor-pointer">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 
                    ${formData.area_completa ? 'bg-purple-600 border-purple-600' : 'border-gray-300 hover:border-purple-400'}`}>
                    {formData.area_completa && <Check className="h-3 w-3 text-white" />}
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.area_completa}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setFormData(prev => ({
                        ...prev,
                        area_completa: isChecked,
                        area_ocupada: isChecked ? prev.area_total : '0'
                      }));
                    }}
                    className="sr-only"
                  />
                  <span>Todo el área</span>
                </label>
              </div>
              <div className="relative">
                <input
                  type="number"
                  name="area_ocupada"
                  value={formData.area_ocupada}
                  onChange={handleAreaOcupadaChange}
                  disabled={formData.area_completa}
                  className={`w-full p-4 border-2 rounded-xl transition-all duration-200 text-lg font-medium
                    ${formData.area_completa 
                      ? 'bg-purple-100 border-purple-200 cursor-not-allowed text-purple-600' 
                      : 'border-gray-200 focus:ring-4 focus:ring-purple-100 focus:border-purple-500 bg-gradient-to-r from-purple-50 to-white hover:border-purple-300'
                    }`}
                  placeholder="0.00"
                  min="0"
                  max={formData.area_total}
                  step="0.01"
                />
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                  <span className={`font-bold ${formData.area_completa ? 'text-purple-600' : 'text-purple-600'}`}>m²</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {formData.tipo !== 'terreno' && (
        <>
          {/* Estructura del inmueble */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="flex items-center gap-3 p-6 bg-gradient-to-r from-emerald-50 to-green-50 border-b border-gray-100 rounded-t-2xl">
              <div className="p-2 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl">
                <Building className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Estructura del Inmueble</h3>
                <p className="text-gray-500 text-sm">Antigüedad, pisos y amenidades disponibles</p>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Datos de estructura */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Antigüedad (años)</label>
                  <div className="relative">
                    <input
                      type="number"
                      name="antiguedad"
                      value={formData.antiguedad}
                      onChange={handleInputChange}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 
                                 focus:border-emerald-500 transition-all duration-200 text-lg font-medium
                                 bg-gradient-to-r from-emerald-50 to-white hover:border-emerald-300"
                      placeholder="0"
                      min="0"
                    />
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                      <Calendar className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Número de Pisos*</label>
                  <div className="relative">
                    <input
                      type="number"
                      name="pisos"
                      value={formData.pisos}
                      onChange={handleInputChange}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 
                                 focus:border-emerald-500 transition-all duration-200 text-lg font-medium
                                 bg-gradient-to-r from-emerald-50 to-white hover:border-emerald-300"
                      placeholder="1"
                      min="1"
                    />
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                      <Layout className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Amenidades */}
              <div className="space-y-4">
                <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  Amenidades Disponibles
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer
                                   hover:border-emerald-300 hover:bg-emerald-50">
                    <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-200
                      ${formData.areas_verdes === "Si" 
                        ? 'bg-emerald-600 border-emerald-600' 
                        : 'border-gray-300 hover:border-emerald-400'}`}>
                      {formData.areas_verdes === "Si" && <Check className="h-4 w-4 text-white" />}
                    </div>
                    <TreePine className="h-5 w-5 text-emerald-600" />
                    <span className="text-sm font-semibold text-gray-700">Áreas Verdes</span>
                    <input
                      type="checkbox"
                      name="areas_verdes"
                      checked={formData.areas_verdes === "Si"}
                      onChange={(e) =>
                        setFormData({ ...formData, areas_verdes: e.target.checked ? "Si" : "No" })
                      }
                      className="sr-only"
                    />
                  </label>
                  
                  <label className="flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer
                                   hover:border-blue-300 hover:bg-blue-50">
                    <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-200
                      ${formData.estacionamiento === "Si" 
                        ? 'bg-blue-600 border-blue-600' 
                        : 'border-gray-300 hover:border-blue-400'}`}>
                      {formData.estacionamiento === "Si" && <Check className="h-4 w-4 text-white" />}
                    </div>
                    <Car className="h-5 w-5 text-blue-600" />
                    <span className="text-sm font-semibold text-gray-700">Estacionamiento</span>
                    <input
                      type="checkbox"
                      name="estacionamiento"
                      checked={formData.estacionamiento === "Si"}
                      onChange={(e) =>
                        setFormData({ ...formData, estacionamiento: e.target.checked ? "Si" : "No" })
                      }
                      className="sr-only"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Características de habitaciones */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
            <div className="flex items-center gap-3 p-6 bg-gradient-to-r from-rose-50 to-pink-50 border-b border-gray-100 rounded-t-2xl">
              <div className="p-2 bg-gradient-to-br from-rose-100 to-rose-200 rounded-xl">
                <Bed className="h-6 w-6 text-rose-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Dormitorios y Baños</h3>
                <p className="text-gray-500 text-sm">Distribución de espacios habitacionales</p>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Habitaciones y baños */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Número de Habitaciones*</label>
                  <div className="relative">
                    <input
                      type="number"
                      name="habitaciones"
                      value={formData.habitaciones}
                      onChange={handleInputChange}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-rose-100 
                                 focus:border-rose-500 transition-all duration-200 text-lg font-medium
                                 bg-gradient-to-r from-rose-50 to-white hover:border-rose-300"
                      placeholder="0"
                      min="0"
                    />
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                      <Bed className="w-5 h-5 text-rose-600" />
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Número de Baños*</label>
                  <div className="relative">
                    <input
                      type="number"
                      name="banos"
                      value={formData.banos}
                      onChange={handleInputChange}
                      className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-rose-100 
                                 focus:border-rose-500 transition-all duration-200 text-lg font-medium
                                 bg-gradient-to-r from-rose-50 to-white hover:border-rose-300"
                      placeholder="0"
                      min="0"
                    />
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                      <Bath className="w-5 h-5 text-rose-600" />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Opciones de habitación principal */}
              {formData.habitaciones > 0 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <div className="w-2 h-2 bg-rose-500 rounded-full"></div>
                    Habitación Principal
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, habitacion_principal_con_bano: true }))}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-3 justify-center
                        ${formData.habitacion_principal_con_bano
                          ? 'border-purple-500 bg-purple-50 shadow-lg scale-105'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'}`}
                    >
                      <div className={`p-2 rounded-lg ${formData.habitacion_principal_con_bano ? 'bg-purple-100' : 'bg-gray-100'}`}>
                        <Bath className={`h-5 w-5 ${
                          formData.habitacion_principal_con_bano 
                            ? 'text-purple-600' 
                            : 'text-gray-500'
                        }`} />
                      </div>
                      <span className={`text-sm font-semibold ${
                        formData.habitacion_principal_con_bano 
                          ? 'text-purple-700' 
                          : 'text-gray-600'
                      }`}>
                        Con baño propio
                      </span>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, habitacion_principal_con_bano: false }))}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-3 justify-center
                        ${!formData.habitacion_principal_con_bano
                          ? 'border-purple-500 bg-purple-50 shadow-lg scale-105'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'}`}
                    >
                      <div className={`p-2 rounded-lg relative ${!formData.habitacion_principal_con_bano ? 'bg-purple-100' : 'bg-gray-100'}`}>
                        <Bath className={`h-5 w-5 ${
                          !formData.habitacion_principal_con_bano 
                            ? 'text-purple-600' 
                            : 'text-gray-500'
                        }`} />
                        {!formData.habitacion_principal_con_bano && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-px h-6 bg-purple-600 transform rotate-45"></div>
                          </div>
                        )}
                      </div>
                      <span className={`text-sm font-semibold ${
                        !formData.habitacion_principal_con_bano 
                          ? 'text-purple-700' 
                          : 'text-gray-600'
                      }`}>
                        Sin baño propio
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CarPublish;